import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react'
import {
    downloadLegalDocumentVersion,
    ICreateLegalDocument,
    ILegalDocument,
    ILegalDocumentVersion,
    legalDocumentsService,
    readFileAsBase64,
    TLegalDocumentStatus,
    TLegalDocumentType,
    TLegalDocumentVersionStatus,
} from '../../../entities/legalDocuments'
import { useGlobalMessageActions } from '../../../entities/globalMessage'
import { FEATURES, hasFeature, useMyActions } from '../../../entities/my'
import { useAppSelector } from '../../../app/store/store'
import { AuthError } from '../../../shared/lib/error/AuthError'
import { MyError } from '../../../shared/lib/error/MyError'
import { MyButtonSecondary } from '../../../shared/ui/button'
import { Modal } from '../../../shared/ui/modal'
import { MyTextarea } from '../../../shared/ui/myTextarea'
import { SurfaceBlock } from '../../../shared/ui/surfaceBlock'
import { Table } from '../../../shared/ui/table'
import classes from './legalDocumentsWidget.module.scss'

type TCreateDocumentForm = {
    type: TLegalDocumentType;
    file: File | null;
}

type TVersionForm = {
    number: string;
    changeNote: string;
    file: File | null;
}

const EMPTY_CREATE_DOCUMENT_FORM: TCreateDocumentForm = {
    type: 'ADMIN_DOCUMENT_TYPE_OFFER',
    file: null,
}

const EMPTY_VERSION_FORM: TVersionForm = {
    number: '',
    changeNote: '',
    file: null,
}

const DOCUMENT_STATUSES: Record<TLegalDocumentStatus, string> = {
    ADMIN_DOCUMENT_STATUS_ACTIVE: 'Активен',
    ADMIN_DOCUMENT_STATUS_HIDDEN: 'Скрыт',
    ADMIN_DOCUMENT_STATUS_ARCHIVED: 'Архивирован',
}

const VERSION_STATUSES: Record<TLegalDocumentVersionStatus, string> = {
    ADMIN_DOCUMENT_VERSION_STATUS_DRAFT: 'Черновик',
    ADMIN_DOCUMENT_VERSION_STATUS_PUBLISHED: 'Опубликована',
    ADMIN_DOCUMENT_VERSION_STATUS_ARCHIVED: 'Архивирована',
}

const DOCUMENT_TYPES: Record<TLegalDocumentType, string> = {
    ADMIN_DOCUMENT_TYPE_OFFER: 'Оферта',
    ADMIN_DOCUMENT_TYPE_PERSONAL_DATA_PROCESSING_POLICY: 'Политика конфиденциальности',
    ADMIN_DOCUMENT_TYPE_PERSONAL_DATA_PROCESSING_CONSENT: 'Согласие на обработку ПД',
}

const DOCUMENT_TYPE_ORDER: TLegalDocumentType[] = [
    'ADMIN_DOCUMENT_TYPE_OFFER',
    'ADMIN_DOCUMENT_TYPE_PERSONAL_DATA_PROCESSING_POLICY',
    'ADMIN_DOCUMENT_TYPE_PERSONAL_DATA_PROCESSING_CONSENT',
]
const MAX_FILE_SIZE = 20 * 1024 * 1024
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

const formatDate = (value: string, withTime = true): string => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return new Intl.DateTimeFormat('ru-RU', withTime
        ? {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'}
        : {day: '2-digit', month: '2-digit', year: 'numeric'}
    ).format(date)
}

const formatSize = (value: string): string => {
    const size = Number(value)
    if (!Number.isFinite(size) || size <= 0) return '—'
    if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} КБ`
    return `${(size / (1024 * 1024)).toFixed(1)} МБ`
}

const versionCountLabel = (count: number | null | undefined): string => {
    if (count === undefined) return 'Считаем количество версий...'
    if (count === null) return 'Количество версий недоступно'

    const remainder = count % 100
    const lastDigit = count % 10
    const word = remainder >= 11 && remainder <= 14
        ? 'версий'
        : lastDigit === 1
            ? 'версия'
            : lastDigit >= 2 && lastDigit <= 4
                ? 'версии'
                : 'версий'

    return `${count} ${word}`
}

const fileMimeType = (file: File): string => {
    if (file.type === 'application/pdf' || file.type === DOCX_MIME) return file.type
    if (file.name.toLowerCase().endsWith('.pdf')) return 'application/pdf'
    if (file.name.toLowerCase().endsWith('.docx')) return DOCX_MIME
    return ''
}

const getFileError = (file: File, pdfOnly = false): string => {
    const mimeType = fileMimeType(file)
    if (!mimeType || (pdfOnly && mimeType !== 'application/pdf')) return pdfOnly ? 'Можно загрузить только PDF.' : 'Можно загрузить только PDF или DOCX.'
    if (file.size > MAX_FILE_SIZE) return 'Размер файла не должен превышать 20 МБ.'
    return ''
}

const statusClass = (status: string): string => {
    switch (status) {
        case 'ADMIN_DOCUMENT_STATUS_ACTIVE':
        case 'ADMIN_DOCUMENT_VERSION_STATUS_PUBLISHED':
            return classes.good
        case 'ADMIN_DOCUMENT_STATUS_HIDDEN':
        case 'ADMIN_DOCUMENT_VERSION_STATUS_DRAFT':
            return classes.warning
        case 'ADMIN_DOCUMENT_STATUS_ARCHIVED':
        case 'ADMIN_DOCUMENT_VERSION_STATUS_ARCHIVED':
            return classes.muted
        default:
            return ''
    }
}

const errorMessage = (error: unknown, fallback: string, conflict: string): string => {
    if (error instanceof MyError) {
        if (error.status === 409) return conflict
        if (error.status === 400) return `Ошибка валидации: ${error.message || fallback}`
        return error.message || fallback
    }
    return fallback
}

export const LegalDocumentsWidget = () => {
    const {my} = useAppSelector(state => state.myReducer)
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const canRead = hasFeature(my, FEATURES.LEGAL_DOCUMENTS.READ)
    const canManage = hasFeature(my, FEATURES.LEGAL_DOCUMENTS.MANAGE)

    const [documents, setDocuments] = useState<ILegalDocument[]>([])
    const [documentVersionCounts, setDocumentVersionCounts] = useState<Record<string, number | null>>({})
    const [isLoading, setIsLoading] = useState(true)
    const [isDocumentsOpen, setIsDocumentsOpen] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [createForm, setCreateForm] = useState<TCreateDocumentForm>(EMPTY_CREATE_DOCUMENT_FORM)
    const [createError, setCreateError] = useState('')
    const [createProgress, setCreateProgress] = useState('')
    const [isCreating, setIsCreating] = useState(false)
    const [selectedDocument, setSelectedDocument] = useState<ILegalDocument | null>(null)
    const [versions, setVersions] = useState<ILegalDocumentVersion[]>([])
    const [isDetailLoading, setIsDetailLoading] = useState(false)
    const [isVersionFormOpen, setIsVersionFormOpen] = useState(false)
    const [versionForm, setVersionForm] = useState<TVersionForm>(EMPTY_VERSION_FORM)
    const [versionError, setVersionError] = useState('')
    const [fileProgress, setFileProgress] = useState('')
    const [isVersionSaving, setIsVersionSaving] = useState(false)
    const [actionLoading, setActionLoading] = useState<string | null>(null)
    const [versionToDelete, setVersionToDelete] = useState<ILegalDocumentVersion | null>(null)

    const reportError = useCallback((error: unknown, fallback: string, conflict: string): string => {
        if (error instanceof AuthError) {
            setIsAuth(false)
        }

        const message = errorMessage(error, fallback, conflict)
        setGlobalMessage({message, type: 'error'})
        return message
    }, [setGlobalMessage, setIsAuth])

    const loadDocuments = useCallback(async () => {
        try {
            setIsLoading(true)
            setLoadError('')
            const receivedDocuments = await legalDocumentsService.listDocuments()
            setDocuments(receivedDocuments)
            const countEntries = await Promise.all(receivedDocuments.map(async document => {
                try {
                    const documentVersions = await legalDocumentsService.listDocumentVersions(document.id)
                    return [document.id, documentVersions.length] as const
                }
                catch {
                    return [document.id, null] as const
                }
            }))
            setDocumentVersionCounts(Object.fromEntries(countEntries))
        }
        catch (error) {
            setLoadError(reportError(error, 'Не удалось получить юридические документы', 'Не удалось получить юридические документы из-за конфликта данных.'))
        }
        finally {
            setIsLoading(false)
        }
    }, [reportError])

    const loadDocumentDetails = useCallback(async (documentId: string) => {
        try {
            setIsDetailLoading(true)
            const [document, receivedVersions] = await Promise.all([
                legalDocumentsService.getDocument(documentId),
                legalDocumentsService.listDocumentVersions(documentId),
            ])
            setSelectedDocument(document)
            setVersions(receivedVersions)
        }
        catch (error) {
            reportError(error, 'Не удалось получить карточку документа', 'Не удалось получить карточку документа из-за конфликта данных.')
            setSelectedDocument(null)
        }
        finally {
            setIsDetailLoading(false)
        }
    }, [reportError])

    useEffect(() => {
        if (canRead) void loadDocuments()
    }, [canRead, loadDocuments])

    if (!canRead) return null

    const openDocument = (document: ILegalDocument, showVersionForm = false) => {
        setSelectedDocument(document)
        setVersions([])
        setVersionForm(EMPTY_VERSION_FORM)
        setVersionError('')
        setIsVersionFormOpen(showVersionForm)
        void loadDocumentDetails(document.id)
    }

    const openFirstVersionForm = (type: TLegalDocumentType) => {
        setCreateForm({...EMPTY_CREATE_DOCUMENT_FORM, type})
        setCreateError('')
        setCreateProgress('')
        setIsCreateOpen(true)
    }

    const closeDocument = () => {
        if (isVersionSaving || actionLoading) return
        setSelectedDocument(null)
        setVersions([])
        setIsVersionFormOpen(false)
        setVersionError('')
        setVersionToDelete(null)
    }

    const createDocument = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!createForm.file) {
            setCreateError('Выберите PDF-файл для юридического документа.')
            return
        }
        const validationError = getFileError(createForm.file, true)
        if (validationError) {
            setCreateError(validationError)
            return
        }

        try {
            setIsCreating(true)
            setCreateError('')
            setCreateProgress('Подготавливаем PDF...')
            const content = await readFileAsBase64(createForm.file)
            setCreateProgress('Создаём документ...')
            const request: ICreateLegalDocument = {type: createForm.type}
            const createdDocument = await legalDocumentsService.createDocument(request)
            setCreateProgress('Загружаем первую версию...')
            await legalDocumentsService.createDocumentVersion(createdDocument.id, {
                number: '1.0',
                fileName: createForm.file.name,
                mimeType: 'application/pdf',
                content,
            })
            setDocuments(current => [createdDocument, ...current])
            setIsCreateOpen(false)
            setCreateForm(EMPTY_CREATE_DOCUMENT_FORM)
            setGlobalMessage({message: 'Юридический документ и его первая версия созданы', type: 'ok'})
            openDocument(createdDocument)
        }
        catch (error) {
            setCreateError(reportError(error, 'Не удалось создать юридический документ или загрузить первую версию', 'Этот тип документа уже создан для текущего admin-контура.'))
            await loadDocuments()
        }
        finally {
            setCreateProgress('')
            setIsCreating(false)
        }
    }

    const selectVersionFile = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null
        if (!file) return
        const error = getFileError(file)
        setVersionError(error)
        setVersionForm(current => ({...current, file: error ? null : file}))
    }

    const selectCreateFile = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null
        if (!file) return
        const error = getFileError(file, true)
        setCreateError(error)
        setCreateForm(current => ({...current, file: error ? null : file}))
    }

    const createVersion = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!selectedDocument || !versionForm.file) {
            setVersionError('Выберите PDF или DOCX для новой версии.')
            return
        }

        const validationError = getFileError(versionForm.file)
        if (validationError) {
            setVersionError(validationError)
            return
        }

        try {
            setIsVersionSaving(true)
            setVersionError('')
            setFileProgress('Подготавливаем файл...')
            const content = await readFileAsBase64(versionForm.file)
            setFileProgress('Загружаем новую версию...')
            await legalDocumentsService.createDocumentVersion(selectedDocument.id, {
                number: versionForm.number.trim(),
                fileName: versionForm.file.name,
                mimeType: fileMimeType(versionForm.file),
                content,
                changeNote: versionForm.changeNote || undefined,
            })
            setVersionForm(EMPTY_VERSION_FORM)
            setIsVersionFormOpen(false)
            setGlobalMessage({message: 'Создан черновик новой версии', type: 'ok'})
            await loadDocumentDetails(selectedDocument.id)
        }
        catch (error) {
            setVersionError(reportError(error, 'Не удалось загрузить новую версию', 'Конфликт номера версии: укажите другой номер.'))
        }
        finally {
            setFileProgress('')
            setIsVersionSaving(false)
        }
    }

    const patchVersionStatus = async (version: ILegalDocumentVersion, status: TLegalDocumentVersionStatus) => {
        if (!selectedDocument) return

        try {
            setActionLoading(`archive-${version.id}`)
            await legalDocumentsService.patchDocumentVersion(version.id, {status})
            setGlobalMessage({message: 'Версия архивирована', type: 'ok'})
            await Promise.all([loadDocumentDetails(selectedDocument.id), loadDocuments()])
        }
        catch (error) {
            reportError(error, 'Не удалось изменить статус версии', 'Конфликт статуса версии. Обновите карточку документа и повторите действие.')
        }
        finally {
            setActionLoading(null)
        }
    }

    const makeVersionPrimary = async (version: ILegalDocumentVersion) => {
        if (!selectedDocument) return

        try {
            setActionLoading(`primary-${version.id}`)
            await legalDocumentsService.makeDocumentVersionPrimary(version.id)
            setGlobalMessage({message: 'Версия сделана основной', type: 'ok'})
            await Promise.all([loadDocumentDetails(selectedDocument.id), loadDocuments()])
        }
        catch (error) {
            reportError(error, 'Не удалось сделать версию основной', 'Конфликт при выборе основной версии. Обновите карточку документа и повторите действие.')
        }
        finally {
            setActionLoading(null)
        }
    }

    const deleteVersion = async () => {
        if (!selectedDocument || !versionToDelete) return

        try {
            setActionLoading(`delete-${versionToDelete.id}`)
            await legalDocumentsService.deleteDocumentVersion(versionToDelete.id)
            setGlobalMessage({message: 'Версия удалена', type: 'ok'})
            setVersionToDelete(null)
            await Promise.all([loadDocumentDetails(selectedDocument.id), loadDocuments()])
        }
        catch (error) {
            reportError(error, 'Не удалось удалить версию', 'Конфликт при удалении версии. Обновите карточку документа и повторите действие.')
        }
        finally {
            setActionLoading(null)
        }
    }

    const downloadVersion = async (version: ILegalDocumentVersion) => {
        try {
            setActionLoading(`download-${version.id}`)
            downloadLegalDocumentVersion(version, await legalDocumentsService.downloadDocumentVersion(version.id))
        }
        catch (error) {
            reportError(error, 'Не удалось скачать версию документа', 'Конфликт при скачивании версии документа.')
        }
        finally {
            setActionLoading(null)
        }
    }

    return (
        <SurfaceBlock className={classes.card}>
            <header className={`${classes.header} ${!isDocumentsOpen ? classes.headerCollapsed : ''}`}>
                <section className={classes.heading}>
                    <span className={classes.icon} aria-hidden="true">
                        <svg viewBox="0 0 24 24"><path d="M7 3H14L19 8V21H7V3Z" /><path d="M14 3V8H19M10 12H16M10 16H16" /></svg>
                    </span>
                    <section>
                        <h2>Юридические документы</h2>
                    </section>
                </section>
                <button
                    type="button"
                    className={classes.collapseButton}
                    onClick={() => setIsDocumentsOpen(value => !value)}
                    aria-expanded={isDocumentsOpen}
                    aria-label={isDocumentsOpen ? 'Свернуть юридические документы' : 'Развернуть юридические документы'}
                >
                    <svg className={isDocumentsOpen ? classes.collapseIconOpen : ''} viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9L12 15L18 9" /></svg>
                </button>
            </header>

            {isDocumentsOpen && (isLoading ? (
                <section className={classes.loading}>Загружаем юридические документы...</section>
            ) : loadError ? (
                <section className={classes.errorState}><p>{loadError}</p><MyButtonSecondary onClick={loadDocuments}>Повторить</MyButtonSecondary></section>
            ) : (
                <section className={classes.documentsGrid}>
                    {DOCUMENT_TYPE_ORDER.map(type => {
                        const document = documents.find(item => item.type === type)

                        return (
                            <article className={classes.documentBlock} key={type}>
                                <section>
                                    <h3>{DOCUMENT_TYPES[type]}</h3>
                                    {document ? (
                                        <>
                                            <section className={`${classes.documentLabels} ${classes.documentBlockMeta}`}>
                                                <span className={`${classes.badge} ${statusClass(document.status)}`}>{DOCUMENT_STATUSES[document.status]}</span>
                                                <span className={`${classes.badge} ${classes.dateBadge}`}><span>Обновлён</span><time>{formatDate(document.updatedAt)}</time></span>
                                            </section>
                                            <p className={classes.versionCount}>Количество версий: <strong>{versionCountLabel(documentVersionCounts[document.id])}</strong></p>
                                        </>
                                    ) : (
                                        <p className={classes.emptyDocument}>Документ пока не добавлен.</p>
                                    )}
                                </section>
                                <section className={classes.documentBlockActions}>
                                    {document && <button type="button" className={classes.openButton} onClick={() => openDocument(document)}>Посмотреть версии</button>}
                                    {canManage && <button type="button" className={classes.primaryButton} onClick={() => document ? openDocument(document, true) : openFirstVersionForm(type)}>Добавить версию</button>}
                                </section>
                            </article>
                        )
                    })}
                </section>
            ))}

            <Modal open={isCreateOpen} setOpen={open => { if (!isCreating) setIsCreateOpen(open) }}>
                <form className={classes.modal} onSubmit={createDocument}>
                    <section className={classes.modalHeader}>
                        <section>
                            <h3>Добавить версию</h3>
                            <p className={classes.modalDescription}>{DOCUMENT_TYPES[createForm.type]}. Документ будет создан автоматически.</p>
                        </section>
                        <button type="button" className={classes.closeButton} onClick={() => setIsCreateOpen(false)} disabled={isCreating} aria-label="Закрыть">×</button>
                    </section>
                    <label className={classes.fileField}>
                        <span>PDF-файл, до 20 МБ</span>
                        <input type="file" accept="application/pdf,.pdf" onChange={selectCreateFile} disabled={isCreating} required />
                        {createForm.file && <strong>{createForm.file.name} · {formatSize(String(createForm.file.size))}</strong>}
                    </label>
                    {createProgress && <p className={classes.fileProgress}>{createProgress}</p>}
                    {createError && <p className={classes.formError}>{createError}</p>}
                    <section className={classes.modalActions}>
                        <MyButtonSecondary onClick={() => setIsCreateOpen(false)} disabled={isCreating}>Отмена</MyButtonSecondary>
                        <button type="submit" className={classes.primaryButton} disabled={isCreating}>{isCreating ? 'Создаём...' : 'Добавить версию'}</button>
                    </section>
                </form>
            </Modal>

            <Modal open={Boolean(selectedDocument)} setOpen={open => { if (!open) closeDocument() }}>
                <section className={`${classes.modal} ${classes.detailsModal}`}>
                    <section className={classes.modalHeader}>
                        <section>
                            <h3>{selectedDocument ? DOCUMENT_TYPES[selectedDocument.type] : 'Юридический документ'}</h3>
                            {selectedDocument && <section className={classes.documentLabels}>
                                <span className={`${classes.badge} ${statusClass(selectedDocument.status)}`}>{DOCUMENT_STATUSES[selectedDocument.status]}</span>
                                <span className={`${classes.badge} ${classes.dateBadge}`}><span>Обновлён</span><time>{formatDate(selectedDocument.updatedAt)}</time></span>
                            </section>}
                        </section>
                        <button type="button" className={classes.closeButton} onClick={closeDocument} disabled={isVersionSaving || Boolean(actionLoading)} aria-label="Закрыть">×</button>
                    </section>

                    {isDetailLoading || !selectedDocument ? (
                        <section className={classes.loading}>Загружаем карточку документа...</section>
                    ) : (
                        <>
                            <section className={classes.versionsHeader}>
                                <section>
                                    <h4>Версии документа</h4>
                                    <p>Действующей считается только отмеченная рабочая версия.</p>
                                </section>
                                {canManage && <button type="button" className={classes.primaryButton} onClick={() => { setVersionForm(EMPTY_VERSION_FORM); setVersionError(''); setIsVersionFormOpen(value => !value) }}>{isVersionFormOpen ? 'Скрыть форму' : 'Загрузить версию'}</button>}
                            </section>

                            {isVersionFormOpen && <form className={classes.versionForm} onSubmit={createVersion}>
                                <section className={classes.formColumns}>
                                    <label className={classes.formField}>
                                        <span>Номер версии</span>
                                        <input value={versionForm.number} onChange={event => setVersionForm(current => ({...current, number: event.target.value}))} placeholder="1.0" maxLength={100} required disabled={isVersionSaving} />
                                    </label>
                                </section>
                                <label className={classes.fileField}>
                                    <span>Файл PDF или DOCX, до 20 МБ</span>
                                    <input type="file" accept="application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx" onChange={selectVersionFile} disabled={isVersionSaving} required />
                                    {versionForm.file && <strong>{versionForm.file.name} · {formatSize(String(versionForm.file.size))}</strong>}
                                </label>
                                <MyTextarea title="Комментарий к изменениям" value={versionForm.changeNote} setValue={changeNote => setVersionForm(current => ({...current, changeNote}))} maxLength={10000} disabled={isVersionSaving} />
                                {fileProgress && <p className={classes.fileProgress}>{fileProgress}</p>}
                                {versionError && <p className={classes.formError}>{versionError}</p>}
                                <section className={classes.modalActions}>
                                    <MyButtonSecondary onClick={() => { setVersionForm(EMPTY_VERSION_FORM); setVersionError(''); setIsVersionFormOpen(false) }} disabled={isVersionSaving}>Отмена</MyButtonSecondary>
                                    <button type="submit" className={classes.primaryButton} disabled={isVersionSaving}>{isVersionSaving ? 'Загружаем...' : 'Создать черновик'}</button>
                                </section>
                            </form>}

                            <section className={classes.tableWrap}>
                                <Table
                                    titles={['Версия', 'Файл', 'Статус', 'Изменения', 'Действия']}
                                    rows={versions.map(version => {
                                        const isWorking = version.id === selectedDocument.currentPublishedVersionId
                                        return [
                                            <section className={classes.versionNumber}><strong>{version.number}</strong>{isWorking && <span className={classes.workingBadge}>Рабочая версия</span>}</section>,
                                            <button type="button" className={classes.fileLink} onClick={() => downloadVersion(version)} disabled={Boolean(actionLoading)}>{version.fileName}<small>{formatSize(version.sizeBytes)}</small></button>,
                                            <span className={`${classes.badge} ${statusClass(version.status)}`}>{VERSION_STATUSES[version.status]}</span>,
                                            version.changeNote || '—',
                                            <section className={classes.rowActions}>
                                                <button type="button" className={classes.iconButton} onClick={() => downloadVersion(version)} disabled={Boolean(actionLoading)} title="Скачать" aria-label={`Скачать ${version.fileName}`}>
                                                    {actionLoading === `download-${version.id}` ? '…' : <svg viewBox="0 0 24 24"><path d="M12 3V15M7 10L12 15L17 10M4 20H20" /></svg>}
                                                </button>
                                                {canManage && !isWorking && <button type="button" className={`${classes.textAction} ${classes.publishAction}`} onClick={() => makeVersionPrimary(version)} disabled={Boolean(actionLoading)}>Сделать основной</button>}
                                                {canManage && version.status === 'ADMIN_DOCUMENT_VERSION_STATUS_PUBLISHED' && <button type="button" className={`${classes.textAction} ${classes.archiveAction}`} onClick={() => patchVersionStatus(version, 'ADMIN_DOCUMENT_VERSION_STATUS_ARCHIVED')} disabled={Boolean(actionLoading)}>Архивировать</button>}
                                                {canManage && <button type="button" className={`${classes.textAction} ${classes.deleteVersionAction}`} onClick={() => setVersionToDelete(version)} disabled={Boolean(actionLoading)}>Удалить</button>}
                                            </section>,
                                        ]
                                    })}
                                />
                            </section>
                        </>
                    )}
                </section>
            </Modal>

            <Modal open={Boolean(versionToDelete)} setOpen={open => { if (!open && !actionLoading) setVersionToDelete(null) }}>
                {versionToDelete && <section className={`${classes.modal} ${classes.confirmModal}`}>
                    <section className={classes.modalHeader}>
                        <h3>Удалить версию {versionToDelete.number}?</h3>
                        <button type="button" className={classes.closeButton} onClick={() => setVersionToDelete(null)} disabled={Boolean(actionLoading)} aria-label="Закрыть">×</button>
                    </section>
                    <p className={classes.confirmationText}>
                        {selectedDocument && versionToDelete.id === selectedDocument.currentPublishedVersionId
                            ? 'Это рабочая версия. После удаления у документа не останется основной версии.'
                            : 'Версия и прикреплённый файл будут удалены без возможности восстановления.'}
                    </p>
                    <section className={classes.modalActions}>
                        <MyButtonSecondary onClick={() => setVersionToDelete(null)} disabled={Boolean(actionLoading)}>Отмена</MyButtonSecondary>
                        <button type="button" className={`${classes.primaryButton} ${classes.deleteButton}`} onClick={deleteVersion} disabled={Boolean(actionLoading)}>{actionLoading ? 'Удаляем...' : 'Удалить'}</button>
                    </section>
                </section>}
            </Modal>
        </SurfaceBlock>
    )
}
